/**
 * Shared report export utilities.
 *
 * exportCSV — builds a CSV blob from headers + rows and triggers a download.
 *
 * PDF export is handled via window.print() + @media print CSS in each report
 * page, using the shared PrintBanner component for the branded header.
 */

// ---------------------------------------------------------------------------
// CSV
// ---------------------------------------------------------------------------

/**
 * @param {string[]}   headers  Column header labels.
 * @param {string[][]} rows     Each element is a row of already-formatted strings.
 * @param {string}     filename Filename for the downloaded file.
 */
/**
 * Format an ISO date string (e.g. "2026-06-25") as "Jun 25, 2026".
 * Parses by splitting on "-" to avoid timezone-offset issues that arise with
 * new Date(iso) on some runtimes.
 *
 * @param {string} iso  ISO date string, e.g. "2026-06-25"
 * @returns {string}    Formatted date, e.g. "Jun 25, 2026"
 */
export function fmtReportDate(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Format an ISO date string (e.g. "2026-06-20") as "MM/DD/YYYY" — the format
 * Excel auto-recognizes as a date cell.
 * Parses by splitting on "-" to avoid timezone-offset issues.
 *
 * @param {string} iso  ISO date string, e.g. "2026-06-20"
 * @returns {string}    Formatted date, e.g. "06/20/2026"
 */
export function fmtCSVDate(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${m.padStart(2, '0')}/${d.padStart(2, '0')}/${y}`;
}

/**
 * Build a standardized CSV filename.
 *
 * @param {string} reportName  Slug, e.g. "bookings", "occupancy_rate", "heatmap"
 * @param {string} from        ISO date string for the range start
 * @param {string} to          ISO date string for the range end
 * @returns {string}           e.g. "bookings_06202026_to_06262026.csv"
 */
export function fmtCSVFilename(reportName, from, to) {
  const fmt = (iso) => {
    const [y, m, d] = iso.split('-');
    return `${m.padStart(2, '0')}${d.padStart(2, '0')}${y}`;
  };
  const fromFmt = fmt(from);
  const toFmt   = fmt(to);
  if (from === to) return `${reportName}_${fromFmt}.csv`;
  return `${reportName}_${fromFmt}_to_${toFmt}.csv`;
}

export function exportCSV(headers, rows, filename = 'report.csv') {
  const lines = [
    headers.join(','),
    ...rows.map(row => row.join(',')),
  ];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

